import is from '@sindresorhus/is';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../context/context';
import { mapToAttributesWithValue } from '../data-contract/custom-attribute';
import { featuredOpportunityOptionsSchema } from '../data-contract/featured-learning-opportunities';
import {
    learningOpportunitiesOptionsSchema,
    learningOpportunitiesResponseSchema,
    learningOpportunityCompletionResponseSchema,
    learningOpportunityListSchema,
    learningOpportunityOptionsSchema,
    learningOpportunitySchema,
} from '../data-contract/learning-opportunities';

import type { Ambition } from '../data-contract/ambition';
import type { CustomAttribute, CustomAttributes } from '../data-contract/custom-attribute';
import type { FeaturedOpportunityOptions } from '../data-contract/featured-learning-opportunities';
import type { FilterRequest } from '../data-contract/filter';
import type {
    LearningOpportunitiesOptions,
    LearningOpportunitiesResponse,
    LearningOpportunity,
    LearningOpportunityCompletionRequest,
    LearningOpportunityCompletionResponse,
    LearningOpportunityCreate,
    LearningOpportunityList,
    LearningOpportunityOptions,
} from '../data-contract/learning-opportunities';
import type { Pathname } from '../http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '../validating-http-fetcher';
import type {
    QueryFunction,
    QueryKey,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

const pathname: Pathname = `/learning-opportunities`;

export type LearningOpportunityMutationResult = UseMutationResult<
    LearningOpportunity,
    Error,
    LearningOpportunityCreate
>;

export type UseDeleteLearningOpportunityMutationResult = UseMutationResult<void, Error, string>;

export type LearningOpportunitiesFilterQueryResult = UseQueryResult<LearningOpportunitiesResponse>;

export const useCreateNewLearningOpportunityMutation = <T extends 'postJson'>(
    operationName: T,
): LearningOpportunityMutationResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (learningOpportunity) => {
            const result = await validatingCobrainerApiFetcher[operationName](learningOpportunitySchema, {
                pathname,
                payload: learningOpportunity,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [pathname, operationName],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [pathname, 'get'] });
        },
    });
};

export const useUpdateLearningOpportunityMutation = <T extends 'patchJson'>(
    operationName: T,
    id: string,
): LearningOpportunityMutationResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (learningOpportunity) => {
            const result = await validatingCobrainerApiFetcher.patchJson(learningOpportunitySchema, {
                pathname: `${pathname}/${id}`,
                payload: learningOpportunity,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [pathname, operationName],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [pathname, 'get'] });
        },
    });
};

export const useLearningOpportunitiesOptionsQuery = (): UseQueryResult<LearningOpportunitiesOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/learning-opportunities/options`;

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(learningOpportunitiesOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
        staleTime: 10_000,
    });
};

export const useLearningOpportunityOptionsQuery = (
    learningOpportunityId: string,
): UseQueryResult<LearningOpportunityOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/learning-opportunities/${learningOpportunityId}/options`;

    return useQuery({
        enabled: is.nonEmptyString(learningOpportunityId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(learningOpportunityOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', learningOpportunityId],
    });
};

export const useFeaturedOpportunityOptionsQuery = (
    featuredOpportunityId: string,
): UseQueryResult<FeaturedOpportunityOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = `/featured-opportunities/${featuredOpportunityId}/options`;

    return useQuery({
        enabled: is.nonEmptyString(featuredOpportunityId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(featuredOpportunityOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', featuredOpportunityId],
    });
};

export const useLearningOpportunityPermissions = (
    learningOpportunityId: string,
): {
    readonly canUserDeleteLearningOpportunity: boolean;
    readonly canUserEditLearningOpportunity: boolean;
    readonly isPending: boolean;
} => {
    const { data: options, isPending } = useLearningOpportunityOptionsQuery(learningOpportunityId);

    const canUserEditLearningOpportunity = options?.canEditLearningOpportunity ?? false;
    const canUserDeleteLearningOpportunity = options?.canDeleteLearningOpportunity ?? false;

    return { canUserDeleteLearningOpportunity, canUserEditLearningOpportunity, isPending };
};

const filterPathname: Pathname = `/learning-opportunities/filter`;

export const useLearningOpportunityFilterQuery = (
    { filter, paged = { page: 0, size: 20 }, sorted }: FilterRequest,
    enabled = true,
): LearningOpportunitiesFilterQueryResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(learningOpportunitiesResponseSchema, {
                pathname: filterPathname,
                payload: {
                    filter,
                    paged,
                    sorted,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [filterPathname, 'postJson', learningOpportunitiesResponseSchema, filter, paged, sorted],
    });
};

export const useLearningOpportunitiesPermissions = (): {
    readonly canUserCreateLearningOpportunities: boolean;
    readonly canUserFilterLearningOpportunities: boolean;
    readonly canUserRecommendLearningOpportunities: boolean;
    readonly canUserSeeLearningOpportunities: boolean;
    readonly isPending: boolean;
} => {
    const { data: options, isPending } = useLearningOpportunitiesOptionsQuery();

    const canUserSeeLearningOpportunities = options?.canManageLearningOpportunities ?? false;
    const canUserCreateLearningOpportunities = options?.canCreateLearningOpportunities ?? false;
    const canUserFilterLearningOpportunities = options?.canManageLearningOpportunities ?? false;
    const canUserRecommendLearningOpportunities = options?.canGetRecommendedLearningOpportunities ?? false;

    return {
        canUserCreateLearningOpportunities,
        canUserFilterLearningOpportunities,
        canUserRecommendLearningOpportunities,
        canUserSeeLearningOpportunities,
        isPending,
    };
};

export const useLearningOpportunitiesCustomAttributes = (): readonly CustomAttribute[] => {
    const { data: options } = useLearningOpportunitiesOptionsQuery();

    return mapToAttributesWithValue(Maybe.of(options?.customAttributes), Maybe.nothing<CustomAttributes>());
};

export const useLearningOpportunityQuery = (
    id: string,
    overrideOptions?: Partial<UseQueryOptions<LearningOpportunity, Error>>,
): UseQueryResult<LearningOpportunity, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(learningOpportunitySchema, {
                pathname: `${pathname}/${id}`,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'get', learningOpportunitySchema, id],
        ...overrideOptions,
    });
};

export const useDeleteLearningOpportunityMutation = (): UseDeleteLearningOpportunityMutationResult => {
    const { cobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `${pathname}/${id}`,
            });

            if (result.isErr) {
                throw result.error;
            }
        },
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: [pathname, 'get'] });
        },
    });
};

const mapFetchLearningRecommendationsBySkillIdQueryOptions = ({
    limit,
    skillId,
    validatingCobrainerApiFetcher,
}: {
    readonly limit?: number;
    readonly skillId: string;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
}): {
    readonly queryFn: QueryFunction<LearningOpportunityList>;
    readonly queryKey: QueryKey;
} => ({
    queryFn: async () => {
        const defaultSearchParams = { skillId };
        const searchParams = is.number(limit)
            ? {
                  ...defaultSearchParams,
                  limit,
              }
            : defaultSearchParams;
        const result = await validatingCobrainerApiFetcher.get(learningOpportunityListSchema, {
            pathname: `${pathname}/recommendations`,
            requestOptions: {
                searchParams,
            },
        });

        if (result.isOk) {
            return result.value;
        }

        throw result.error;
    },
    queryKey: ['get', pathname, skillId, limit, validatingCobrainerApiFetcher, learningOpportunityListSchema],
});

export const useLearningOpportunityRecommendationsByAmbitionQuery = ({
    ambition,
    enabled,
    limit,
}: {
    readonly ambition?: Ambition;
    readonly enabled: boolean;
    readonly limit?: number;
}): UseQueryResult<LearningOpportunityList, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        enabled,
        ...mapFetchLearningRecommendationsBySkillIdQueryOptions({
            limit,
            skillId: ambition?.id ?? '',
            validatingCobrainerApiFetcher,
        }),
    });
};

export const useLearningOpportunityRecommendationsByAmbitionsQueries = (
    ambitions: readonly Ambition[],
    enabled: boolean,
    limit?: number,
): readonly UseQueryResult<LearningOpportunityList>[] => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQueries({
        queries: ambitions.map((ambition) => ({
            enabled,
            ...mapFetchLearningRecommendationsBySkillIdQueryOptions({
                limit,
                skillId: ambition?.id ?? '',
                validatingCobrainerApiFetcher,
            }),
        })),
    });
};

export const useLearningOpportunityRecommendationsBySkillIdQueries = (
    skillIds: readonly string[],
    enabled: boolean,
    limit?: number,
): readonly UseQueryResult<LearningOpportunityList>[] => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQueries({
        queries: skillIds.map((skillId) => ({
            enabled,
            ...mapFetchLearningRecommendationsBySkillIdQueryOptions({
                limit,
                skillId,
                validatingCobrainerApiFetcher,
            }),
        })),
    });
};

export const useCompleteLearningOpportunity = (
    id: string,
): UseMutationResult<LearningOpportunityCompletionResponse, Error, void> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const payload: LearningOpportunityCompletionRequest = {
        addSkillsToUser: true,
        generateUserCourseHistoryEntry: true,
    };

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(learningOpportunityCompletionResponseSchema, {
                pathname: `${pathname}/${id}/completion`,
                payload,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
    });
};
